import { Paper } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useContextMenu } from 'mantine-contextmenu';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import type { SerializableRenderTreeNode } from '../../../../../../react-commons/components/data-display/tree/RenderTreeNode';
import { Tree } from '../../../../../../react-commons/components/data-display/tree/Tree';
import { TreeHoverButton } from '../../../../../../react-commons/components/inputs/buttons/TreeHoverButton';
import { useNestedDialogFunction } from '../../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { SplitPane } from '../../../../../../react-commons/views/split-pane/SplitPane';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    type SystemUserGroupResource,
    useSystemUserGroupActions,
} from '../../../../resource/resources/persistables/user/systemUserGroupResource';
import { CreateSystemUserGroupDialog } from '../../../page-components/system-user-management/system-user-group/CreateSystemUserGroupDialog';
import { SystemUserGroupView } from '../../../page-components/system-user-management/system-user-group/SystemUserGroupView';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { NothingSelectedComponent } from '../../../reusable-components/NothingSelectedComponent';
import { checkUnsavedData } from '../../../util/checkUnsavedData';
import { defaultLoadingNode } from '../../../util/parseTreeFunctions';
import { selectIsDirty } from '../../../util/useSelectorFunctions';

type SystemUserGroupsPageTabTreeNode = SerializableRenderTreeNode<SystemUserGroupResource>;

const parseSystemUserGroups = (
    groupOutlines: QueryObserverResult<Array<SystemUserGroupResource> | null>
): Array<SystemUserGroupsPageTabTreeNode> => {
    return (
        groupOutlines.data?.map((group) => ({
            key: group.id,
            label: group.name,
            data: group,
        })) ?? [defaultLoadingNode]
    );
};

export const SystemUserGroupsPageTab = () => {
    const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

    const isDirty = useSelector(selectIsDirty);

    const nestedDialog = useNestedDialogFunction();
    const { showContextMenu } = useContextMenu();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();

    const systemUserGroupActions = useSystemUserGroupActions();

    const deleteSystemUserGroup = systemUserGroupActions.useDeleteResource();
    const systemUserGroupsQuery = systemUserGroupActions.useGetAllResources();

    const nodes = useMemo(() => {
        return parseSystemUserGroups(systemUserGroupsQuery);
    }, [isDirty, systemUserGroupsQuery.data]);

    const openAddDialog = () => {
        nestedDialog.showNestedDialog(<CreateSystemUserGroupDialog onClose={nestedDialog.closeNestedDialog} />);
    };

    const deleteGroup = (id: string, name: string) => {
        deleteResourceDialogFunction.deleteResource(
            id,
            name,
            ResourceType.SYSTEM_USER_GROUP,
            deleteSystemUserGroup,
            () => setSelectedId(undefined)
        );
    };

    const getMenuItems = (node: SystemUserGroupsPageTabTreeNode) => {
        return [
            {
                icon: <IconTrash />,
                key: 'delete',
                title: 'Delete System User Group',
                onClick: () => deleteGroup(node.key, node.label),
            },
        ];
    };

    return (
        <Paper className={'main-window-inner-content'} variant={'outlined'} h={'calc(100% - 30px)'}>
            <SplitPane secondaryInitialSize={70}>
                <Tree
                    nodes={nodes}
                    filtered
                    selectedNodeKey={selectedId}
                    onNodeSelected={(node) => checkUnsavedData(isDirty, () => setSelectedId(node.key))}
                    onContextMenu={(selection) => showContextMenu(getMenuItems(selection.node))(selection.event)}
                    onAddClick={openAddDialog}
                    getElements={(node) => ({
                        hoverNode: (
                            <TreeHoverButton
                                Icon={IconTrash}
                                onClick={() => deleteGroup(node.data.id, node.data.name)}
                            />
                        ),
                    })}
                />
                <div
                    style={{
                        display: 'inline-block',
                        verticalAlign: 'top',
                        overflowY: 'auto',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <Show when={selectedId} fallback={<NothingSelectedComponent />}>
                        {(systemUserGroupId) => <SystemUserGroupView id={systemUserGroupId} />}
                    </Show>
                </div>
            </SplitPane>
        </Paper>
    );
};
